import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { AtividadeFormSchema, type AtividadeFormInput, type Atividade } from '@azoupvendas/shared';
import api from '../../services/auth.service.js';
import type { ClienteRow } from '../../hooks/useClientes.js';

// AIDEV-NOTE: Formulario de criacao/edicao de Atividade com vinculo opcional a Lead ou Cliente

interface AtividadeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AtividadeFormInput) => Promise<void>;
  leadId?: string;
  clienteCodigo?: number;
  /** Quando fornecido, abre o formulário em modo de edição pré-preenchido */
  initialData?: Atividade;
}

const TIPOS = [
  { value: 'ligacao', label: '📞 Ligação' },
  { value: 'email', label: '✉️ E-mail' },
  { value: 'tarefa', label: '✅ Tarefa' },
  { value: 'reuniao', label: '🤝 Reunião' }
];

export function AtividadeForm({
  open,
  onClose,
  onSubmit,
  leadId,
  clienteCodigo,
  initialData
}: AtividadeFormProps) {
  const isEditing = !!initialData;
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Campos de data e hora separados para melhor UX mobile
  const [dataParte, setDataParte] = useState('');
  const [horaParte, setHoraParte] = useState('');

  // Busca de cliente (só exibe quando clienteCodigo não foi pré-definido)
  const [clienteBusca, setClienteBusca] = useState('');
  const [clienteOpcoes, setClienteOpcoes] = useState<ClienteRow[]>([]);
  const [clienteSelecionado, setClienteSelecionado] = useState<ClienteRow | null>(null);
  const [carregandoClientes, setCarregandoClientes] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<AtividadeFormInput>({
    resolver: zodResolver(AtividadeFormSchema),
    defaultValues: { tipo: 'ligacao', lead_id: leadId, cliente_codigo: clienteCodigo }
  });

  // Pré-preenche campos quando abre em modo edição
  useEffect(() => {
    if (open && initialData) {
      // AIDEV-NOTE: ?? undefined converte null (Supabase) para undefined — Zod .optional() rejeita null
      reset({
        tipo: initialData.tipo,
        descricao: initialData.descricao ?? undefined,
        lead_id: initialData.lead_id ?? undefined,
        cliente_codigo: initialData.cliente_codigo ?? undefined
      });
      if (initialData.data_prevista) {
        const dt = new Date(initialData.data_prevista);
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        const hh = String(dt.getHours()).padStart(2, '0');
        const min = String(dt.getMinutes()).padStart(2, '0');
        setDataParte(`${yyyy}-${mm}-${dd}`);
        setHoraParte(`${hh}:${min}`);
      }
      if (initialData.cliente_razao_social && initialData.cliente_codigo) {
        setClienteSelecionado({
          codigo: initialData.cliente_codigo,
          razao_social: initialData.cliente_razao_social,
          cnpj_cpf: ''
        } as ClienteRow);
        setClienteBusca(initialData.cliente_razao_social);
      }
    }
    if (!open) {
      setDataParte('');
      setHoraParte('');
    }
  }, [open, initialData, reset]);

  // AIDEV-NOTE: Debounce 400ms na busca de clientes — evita query a cada tecla
  useEffect(() => {
    if (clienteCodigo !== undefined) return;
    const timer = setTimeout(async () => {
      if (!clienteBusca.trim()) {
        setClienteOpcoes([]);
        return;
      }
      setCarregandoClientes(true);
      try {
        const res = await api.get<{ clientes: ClienteRow[] }>('/clientes', {
          params: { q: clienteBusca.trim(), limit: 10, situacao: 'A' }
        });
        setClienteOpcoes(res.data.clientes);
      } catch {
        setClienteOpcoes([]);
      } finally {
        setCarregandoClientes(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [clienteBusca, clienteCodigo]);

  const handleClose = () => {
    reset({ tipo: 'ligacao', lead_id: leadId, cliente_codigo: clienteCodigo });
    setSubmitError(null);
    setDataParte('');
    setHoraParte('');
    setClienteBusca('');
    setClienteOpcoes([]);
    setClienteSelecionado(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form
        onSubmit={handleSubmit(async (data) => {
          try {
            setSubmitError(null);
            // AIDEV-NOTE: new Date() no browser interpreta como hora LOCAL e .toISOString() converte para UTC
            // Sem isso, o servidor (UTC) trataria a string sem timezone como UTC, causando offset de 3h
            const dataPrevista = dataParte
              ? new Date(`${dataParte}T${horaParte || '00:00'}`).toISOString()
              : undefined;
            await onSubmit({ ...data, data_prevista: dataPrevista });
            handleClose();
          } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Erro ao salvar. Tente novamente.');
          }
        })}
      >
        <DialogTitle>{isEditing ? 'Editar Atividade' : 'Nova Atividade'}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Seletor de cliente — só exibe quando não pré-definido via prop */}
            {clienteCodigo === undefined && (
              <Grid item xs={12}>
                <Autocomplete
                  options={clienteOpcoes}
                  getOptionLabel={(o) => `${o.razao_social} (${o.cnpj_cpf})`}
                  isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
                  loading={carregandoClientes}
                  value={clienteSelecionado}
                  onChange={(_, value) => {
                    setClienteSelecionado(value);
                    setValue('cliente_codigo', value?.codigo ?? undefined);
                  }}
                  inputValue={clienteBusca}
                  onInputChange={(_, value) => setClienteBusca(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente (opcional)"
                      placeholder="Digite para buscar..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {carregandoClientes && <CircularProgress size={18} />}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  noOptionsText={
                    clienteBusca.trim() ? 'Nenhum cliente encontrado' : 'Digite para buscar'
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="tipo"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Tipo *" fullWidth>
                    {TIPOS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Data e hora separados — melhor UX no mobile */}
            <Grid item xs={7} sm={4}>
              <TextField
                label="Data prevista"
                type="date"
                fullWidth
                value={dataParte}
                onChange={(e) => setDataParte(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!errors.data_prevista}
                helperText={errors.data_prevista?.message}
              />
            </Grid>
            <Grid item xs={5} sm={2}>
              <TextField
                label="Hora"
                type="time"
                fullWidth
                value={horaParte}
                onChange={(e) => setHoraParte(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="descricao"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Descrição" fullWidth multiline rows={2} />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
